import React, { useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import {
  mdiBarcodeScan,
  mdiClose,
  mdiAlertCircleOutline,
  mdiFileDelimitedOutline,
  mdiUploadOutline,
  mdiDownloadOutline,
} from "@mdi/js";
import Papa from "papaparse";
import { fetchGet } from "../../content_panel/services/serviceClient";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import {
  ScannedBarCodeScanForDeviceFromPTAPI,
  ScannedBarCodeScanForDeviceIDFromPTAPI,
} from "../../../../erp/inventory/stock_transfer/sender_stock_transfer/service/stock_transfer_repo";
import { QrScanner } from "@yudiel/react-qr-scanner";
import DeviceIdsViewer from "../common-components/DeviceIdsViewer";

type Props = {
  open: boolean;
  from_warehouse_id: string;
  item_code: string;
  requested_qty: number;
  onClose: () => void;
  onSave: (devices: string[]) => void;
};

const DeviceAllocationModal: React.FC<Props> = ({
  open,
  from_warehouse_id,
  item_code,
  requested_qty,
  onClose,
  onSave,
}) => {
  const { showToast } = useToast();
  const normalizeDeviceId = (value: any) => String(value ?? "").trim();

  const [activeTab, setActiveTab] = useState<"scan" | "bulk" | "upload">(
    "scan",
  );
  const [devices, setDevices] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [availableCount, setAvailableCount] = useState(0);
  const [barcodeInputValue, setBarcodeInputValue] = useState("");
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const scanLockRef = useRef(false);

  const availableIdsRef = useRef<string[]>([]);
  const availableDevicesFullRef = useRef<any[]>([]);

  useEffect(() => {
    if (devices.length === requested_qty && requested_qty > 0) {
      showToast({
        type: ToastType.success,
        text: "Required devices scanned",
      });
    }
  }, [devices.length, requested_qty]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const clean = barcodeInputValue.trim();
      if (clean && !loading && !scanLockRef.current) {
        if (clean.length === 8 || clean.length === 6) {
          handleScanResult(clean);
          setBarcodeInputValue("");
        }
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [barcodeInputValue, loading]);

  useEffect(() => {
    if (!open) return;
    setDevices([]);
    fetchAvailableDevices();
  }, [open, from_warehouse_id, item_code]);

  if (!open) return null;

  /* ---------------------------------- api ---------------------------------- */

  const fetchAvailableDevices = async () => {
    if (!from_warehouse_id || !item_code) {
      showToast({
        type: ToastType.warning,
        text: "Warehouse and Item Code are required",
      });
      return;
    }

    try {
      setLoading(true);
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/items/unique-ids?warehouse_id=${from_warehouse_id}&item_code=${item_code}`;
      const res = await fetchGet(url);

      if (!res.success) {
        showToast({ type: ToastType.error, text: res.error });
        return;
      }

      const fullData = res.result ?? [];
      const ids = fullData
        .map((r: any) => normalizeDeviceId(r.unique_id))
        .filter(Boolean);

      availableIdsRef.current = Array.from(new Set(ids));
      availableDevicesFullRef.current = fullData;
      setAvailableCount(availableIdsRef.current.length);
    } catch {
      showToast({
        type: ToastType.error,
        text: "Failed to fetch available devices",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleScanResult = async (scannedValue: string) => {
    if (scanLockRef.current) return;

    const id = normalizeDeviceId(scannedValue);
    if (!id) return;

    scanLockRef.current = true;

    if (![6, 8].includes(id.length)) {
      showToast({
        type: ToastType.error,
        text: "Please enter a valid scanned ID (6 or 8 characters)",
      });
      scanLockRef.current = false;
      return;
    }

    if (devices.includes(id)) {
      showToast({
        type: ToastType.warning,
        text: "Device already scanned",
      });
      scanLockRef.current = false;
      return;
    }

    try {
      setLoading(true);

      let res;
      if (id.length === 8) {
        res = await ScannedBarCodeScanForDeviceFromPTAPI(
          item_code,
          from_warehouse_id,
          id,
        );
      } else if (id.length === 6) {
        res = await ScannedBarCodeScanForDeviceIDFromPTAPI(
          item_code,
          from_warehouse_id,
          id,
        );
      }

      if (!res.success) {
        showToast({ type: ToastType.error, text: res.error });
        return;
      }

      const allDevices: string[] = Array.from(
        new Set(
          (res.devices ?? [])
            .map((d: any) => normalizeDeviceId(d.unique_id))
            .filter(Boolean),
        ),
      );

      const availableSet = new Set(availableIdsRef.current);
      const validDevices: string[] = allDevices.filter((uid) =>
        availableSet.has(uid),
      );

      const invalidDevices = allDevices.filter((uid) => !availableSet.has(uid));

      if (invalidDevices.length > 0) {
        showToast({
          type: ToastType.error,
          text: `Invalid device(s): ${invalidDevices.join(", ")}`,
        });
        return;
      }

      let addedCount = 0;
      setDevices((prev) => {
        const prevSet = new Set(prev);
        const toAdd = validDevices.filter((uid) => !prevSet.has(uid));
        addedCount = toAdd.length;
        return [...prev, ...toAdd];
      });

      if (addedCount > 0) {
        showToast({
          type: ToastType.success,
          text: res.message || "Device scanned successfully",
        });
      } else if (validDevices.length > 0) {
        showToast({
          type: ToastType.error,
          text: "Same box is scanned twice",
        });
      }
    } finally {
      setLoading(false);

      setTimeout(() => {
        scanLockRef.current = false;
      }, 800);
    }
  };

  const handleClearAll = () => {
    if (
      !window.confirm(
        "Are you sure you want to clear all scanned devices? This action cannot be undone.",
      )
    ) {
      return;
    }

    setDevices([]);
    showToast({
      type: ToastType.success,
      text: "All devices cleared",
    });
  };

  const handleSave = () => {
    if (devices.length !== requested_qty) {
      showToast({
        type: ToastType.error,
        text: `Please add exactly ${requested_qty} devices (currently ${devices.length})`,
      });
      return;
    }

    // Availability validation
    const invalid = devices.filter(
      (id) => !availableIdsRef.current.includes(id),
    );

    if (invalid.length) {
      showToast({
        type: ToastType.error,
        text: `Invalid devices detected: ${invalid.join(", ")}`,
      });
      return;
    }

    showToast({
      type: ToastType.success,
      text: "Devices successfully allocated",
    });

    //  Close modal
    onClose();

    // Success
    onSave(devices);
  };

  const handleCSVUpload = (file: File) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = result.data as any[];
        if (!rows.length) {
          showToast({
            type: ToastType.error,
            text: "CSV is empty or invalid",
          });
          return;
        }

        const required = ["Item Code", "Unique ID", "Item Name"];
        const missing = required.filter((h) => !(h in rows[0]));
        if (missing.length) {
          showToast({
            type: ToastType.error,
            text: `Missing columns: ${missing.join(", ")}`,
          });
          return;
        }

        const ids = rows.map((r) => r["Unique ID"]).filter(Boolean);

        const invalid = ids.filter(
          (id) => !availableIdsRef.current.includes(id),
        );
        if (invalid.length) {
          showToast({
            type: ToastType.error,
            text: `Invalid Device IDs: ${invalid.join(", ")}`,
          });
          return;
        }

        setDevices((prev) => Array.from(new Set([...prev, ...ids])));
        showToast({
          type: ToastType.success,
          text: `${ids.length} devices added`,
        });
      },
    });
  };

  const downloadAvailableDevices = () => {
    const devices = availableDevicesFullRef.current;
    if (!devices.length) {
      showToast({ type: ToastType.info, text: "No devices available" });
      return;
    }

    // Group by group_box_id (null = ungrouped / device-box only)
    const grouped = new Map<string | null, any[]>();
    for (const d of devices) {
      const key = d.group_box_id ?? null;
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(d);
    }

    const rows: string[][] = [["Group Box ID", "Device Box ID", "Device ID"]];

    // First: entries with a group_box_id, sorted
    const groupKeys = Array.from(grouped.keys())
      .filter((k) => k !== null)
      .sort() as string[];
    for (const gKey of groupKeys) {
      const items = grouped.get(gKey)!;
      for (let i = 0; i < items.length; i++) {
        rows.push([
          i === 0 ? gKey : "",
          items[i].device_box_id ?? "",
          items[i].unique_id ?? "",
        ]);
      }
    }

    // Then: ungrouped (only in device-boxes, no group box)
    const ungrouped = grouped.get(null);
    if (ungrouped?.length) {
      for (const d of ungrouped) {
        rows.push(["", d.device_box_id ?? "", d.unique_id ?? ""]);
      }
    }

    const csv =
      "\uFEFF" + rows.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `device_ids_${item_code}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  };

  /* ---------------------------------- render ---------------------------------- */

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black bg-opacity-40"
        onClick={(e) => e.stopPropagation()} // REQUIRED
      />

      {/* Modal */}
      <div
        id="warehouse-outward-device-allocation-modal"
        className="fixed z-50 rounded-xl bg-white p-6 shadow-2xl border border-gray-200"
        style={{
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: "40rem",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-xl"> */}
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Icon path={mdiBarcodeScan} size={0.9} />
            Add Device IDs – {item_code}
          </h2>
          <button
            id="warehouse-outward-device-allocation-close-icon-btn"
            onClick={onClose}
          >
            <Icon path={mdiClose} size={0.9} />
          </button>
        </div>

        {/* Item Info */}
        <div className="rounded-lg bg-gray-50 border p-4 flex justify-between items-center">
          <div className="flex items-center gap-3 text-sm font-medium">
            Available: {availableCount}
            <button
              id="warehouse-outward-device-allocation-download-available-btn"
              onClick={downloadAvailableDevices}
              disabled={availableCount === 0}
            >
              <Icon path={mdiDownloadOutline} size={0.7} />
            </button>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold px-2 py-1 rounded-full bg-gray-200">
            <Icon path={mdiAlertCircleOutline} size={0.6} />
            {devices.length} / {requested_qty}
          </div>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-3 bg-gray-100 rounded-md mt-4">
          {[
            { key: "scan", label: "Scan", icon: mdiBarcodeScan },
            { key: "bulk", label: "Bulk Paste", icon: mdiFileDelimitedOutline },
            { key: "upload", label: "Upload CSV", icon: mdiUploadOutline },
          ].map((t) => (
            <button
              key={t.key}
              id={`warehouse-outward-device-allocation-tab-${t.key}`}
              onClick={() => setActiveTab(t.key as any)}
              className={`flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md ${
                activeTab === t.key ? "bg-white shadow" : "text-gray-500"
              }`}
            >
              <Icon path={t.icon} size={0.7} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Scan */}
        {activeTab === "scan" && (
          <div className="mt-4 space-y-4">
            {/* Hardware Barcode Scanner Input */}
            <div className="space-y-2 bg-blue-50/50 p-4 rounded-lg border border-blue-100">
              <div className="flex items-center gap-2 mb-1">
                <Icon
                  path={mdiBarcodeScan}
                  size={0.6}
                  className="text-blue-600"
                />
                <label className="text-xs font-bold text-gray-700">
                  Hardware Barcode Scanner
                </label>
              </div>
              <div className="flex w-full">
                <input
                  id="warehouse-outward-device-allocation-barcode-input"
                  ref={barcodeInputRef}
                  type="text"
                  autoFocus
                  value={barcodeInputValue}
                  onChange={(e) => setBarcodeInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && barcodeInputValue.trim()) {
                      handleScanResult(barcodeInputValue.trim());
                      setBarcodeInputValue("");
                      setTimeout(() => barcodeInputRef.current?.focus(), 100);
                    }
                  }}
                  onBlur={() => {
                    if (devices.length < requested_qty) {
                      setTimeout(() => barcodeInputRef.current?.focus(), 200);
                    }
                  }}
                  placeholder="Scan or type device ID..."
                  className="w-full rounded-md border-2 border-primary/30 p-3 text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all shadow-inner bg-white"
                />
              </div>
            </div>

            {/* Currently Scanned Devices - above camera */}
            {devices.length > 0 && (
              <div className="bg-white border border-purple-200 rounded-lg p-1 space-y-2">
                <div className="flex justify-between items-center px-3 pt-2">
                  <p className="text-xs font-bold text-purple-700 uppercase">
                    Current Session Scans
                  </p>
                  <button
                    id="warehouse-outward-device-allocation-clear-all-scan-btn"
                    onClick={handleClearAll}
                    className="text-[10px] font-bold text-red-500 hover:text-red-700"
                  >
                    Clear All
                  </button>
                </div>
                <DeviceIdsViewer
                  deviceIds={devices}
                  title={`Currently Scanned Devices`}
                  defaultOpen
                  onRemove={(id) =>
                    setDevices((prev) => prev.filter((d) => d !== id))
                  }
                />
              </div>
            )}

            <div className="relative flex items-center py-2">
              <div className="flex-grow border-t border-gray-200"></div>
              <span className="flex-shrink mx-4 text-gray-400 text-[10px] font-bold uppercase tracking-wider">
                or scan via camera
              </span>
              <div className="flex-grow border-t border-gray-200"></div>
            </div>

            <div className="rounded-md border-2 border-gray-100 overflow-hidden h-[300px] max-w-sm mx-auto relative group bg-black shadow-lg">
              <QrScanner
                constraints={{ facingMode: "environment" }}
                onDecode={(result) => {
                  if (result && !loading) handleScanResult(result);
                }}
                onError={(err) => {
                  console.error(err);
                  showToast({
                    type: ToastType.error,
                    text: "Camera error",
                  });
                }}
                containerStyle={{
                  width: "100%",
                  height: "100%",
                }}
                videoStyle={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
              {loading && (
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                  <div className="bg-white rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2 shadow-xl border border-blue-200">
                    <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bulk */}
        {activeTab === "bulk" && (
          <textarea
            id="warehouse-outward-device-allocation-bulk-textarea"
            rows={5}
            className="w-full mt-4 rounded-md border p-3 text-sm"
            placeholder="One device ID per line"
            onBlur={(e) => {
              const ids = e.target.value
                .split("\n")
                .map((v) => v.trim())
                .filter((id) => availableIdsRef.current.includes(id));
              setDevices((prev) => Array.from(new Set([...prev, ...ids])));
            }}
          />
        )}

        {/* Upload */}
        {activeTab === "upload" && (
          <input
            id="warehouse-outward-device-allocation-csv-upload-input"
            type="file"
            accept=".csv"
            className="mt-4"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleCSVUpload(file);
            }}
          />
        )}

        {/* Currently Scanned Devices for non-scan tabs */}
        {activeTab !== "scan" && devices.length > 0 && (
          <div className="bg-white border border-purple-200 rounded-lg p-1 mt-4 space-y-2">
            <div className="flex justify-between items-center px-3 pt-2">
              <p className="text-xs font-bold text-purple-700 uppercase">
                Current Session Scans
              </p>
              <button
                id="warehouse-outward-device-allocation-clear-all-other-btn"
                onClick={handleClearAll}
                className="text-[10px] font-bold text-red-500 hover:text-red-700"
              >
                Clear All
              </button>
            </div>
            <DeviceIdsViewer
              deviceIds={devices}
              title={`Currently Scanned Devices`}
              onRemove={(id) =>
                setDevices((prev) => prev.filter((d) => d !== id))
              }
            />
          </div>
        )}

        {/* Footer */}
        <div className="flex gap-3 mt-6">
          <button
            id="warehouse-outward-device-allocation-cancel-btn"
            className="flex-1 h-10 rounded-md border text-sm font-medium"
            onClick={onClose}
          >
            Cancel
          </button>
          {devices.length > 0 && (
            <button
              id="warehouse-outward-device-allocation-footer-clear-all-btn"
              onClick={handleClearAll}
              className="flex-1 h-10 rounded-md border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 transition"
            >
              Clear All
            </button>
          )}
          <button
            id="warehouse-outward-device-allocation-save-btn"
            onClick={handleSave}
            className="flex-1 h-10 rounded-md bg-blue-600 text-white text-sm font-medium disabled:opacity-50"
          >
            Save Device IDs
          </button>
        </div>
      </div>
    </>
  );
};

export default DeviceAllocationModal;
